import { concatMap, map, withLatestFrom, catchError } from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'
import { normalizeToSchema } from 'src/app/shared/normalization/normalization'
import { normalizationActions } from 'src/app/shared/normalization/normalization.actions'
import { ResourceEffects } from 'src/app/shared/resource-effects'

import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'

import { Sale, StatusCode } from './sale'
import { saleActions } from './sale.actions'
import * as fromSale from './sale.reducer'
import { SaleService } from './sale.service'
import { authActions } from 'src/app/core/auth/auth.actions'
import { CondOperator, RequestQueryBuilder } from '@nestjsx/crud-request'
import { of } from 'rxjs'
import { configurationOptionSelectors } from '../configuration-option/configuration-option.selectors'
import { ConfigOptionsKeys } from 'src/app/shared/constants'
import { Answer } from '../survey/answer/answer'
import { LocalStorageKeys } from 'src/app/core/local-storage.keys'
import { LocalStorageService } from 'src/app/core/local-storage.service'
import { SaleItem } from './sale-item/sale-item'

@Injectable()
export class SaleEffects extends ResourceEffects<Sale> {
  constructor(
    public actions$: Actions,
    public store: Store<AppState>,
    public saleService: SaleService,
    public router: Router,
    private localStorageService: LocalStorageService
  ) {
    super(fromSale.FEATURE_NAME, saleService, saleActions, actions$)
  }

  createOne = createEffect(() =>
    this.actions$.pipe(
      ofType(saleActions.createOne),
      withLatestFrom(
        this.store.select(
          configurationOptionSelectors.selectByKeyWithType(
            ConfigOptionsKeys.ORGANISATION_BOOKING_ENABLED
          )
        )
      ),
      concatMap(([action, organisationBookingEnabled]) => {
        const entity = {
          ...action.entity,
          organizationId:
            organisationBookingEnabled === true
              ? action.entity.organizationId
              : undefined
        }

        return this.saleService.createOne(entity).pipe(
          map((sale) => {
            this.persistPendingSurveyAnswers(sale, action.entity.saleItems)

            this.store.dispatch(
              saleActions.actionFinished({
                actionFinished: { action: action.type, data: [sale] }
              })
            )
            return normalizationActions.upsertMany({
              entities: normalizeToSchema({
                [this.entityFeatureKey]: [sale]
              })
            })
          }),
          catchError((error) => {
            console.error('Failed to create sale', error)
            return of(
              saleActions.createOneFailed({
                entity: action.entity,
                error: error?.error?.message
              })
            )
          })
        )
      })
    )
  )

  private persistPendingSurveyAnswers(
    sale: Sale,
    submittedSaleItems: any[] = []
  ) {
    if (!sale?.id) {
      return
    }

    const saleItems = (sale.saleItems as SaleItem[])?.some(
      (saleItem) => saleItem?.params?.surveyAnswers?.length
    )
      ? sale.saleItems
      : submittedSaleItems

    if (!saleItems.length) {
      return
    }

    const surveyAnswers = saleItems.flatMap((saleItem) =>
      (saleItem?.params?.surveyAnswers ?? [])
        .filter((answer: Partial<Answer>) => Boolean(answer?.questionId))
        .map((answer: Partial<Answer>) => ({
          ...answer,
          memberId: answer.memberId ?? sale.customerPersonId ?? null,
          saleId: sale.id,
          bookingId:
            answer.bookingId ??
            saleItem.bookingId ??
            saleItem.params?.bookingId ??
            null,
          courseId: answer.courseId ?? saleItem.courseId ?? null
        }))
    ) as Partial<Answer>[]

    if (!surveyAnswers.length) {
      return
    }

    const pendingSurveyAnswersBySaleId = this.getPendingSurveyAnswers()

    pendingSurveyAnswersBySaleId[String(sale.id)] = surveyAnswers
    this.localStorageService.setItem(
      LocalStorageKeys.PENDING_SURVEY_ANSWERS,
      pendingSurveyAnswersBySaleId
    )
  }

  private getPendingSurveyAnswers(): Record<string, Partial<Answer>[]> {
    const pendingSurveyAnswers = this.localStorageService.getItem(
      LocalStorageKeys.PENDING_SURVEY_ANSWERS
    )

    if (!pendingSurveyAnswers) {
      return {}
    }

    try {
      return JSON.parse(pendingSurveyAnswers)
    } catch {
      return {}
    }
  }

  updateOneById = createEffect(() =>
    this.actions$.pipe(
      ofType(saleActions.updateOneById),
      concatMap((action) => {
        return this.saleService
          .updateOne(action.id, action.params as Partial<Sale>)
          .pipe(
            map((sale) => {
              this.store.dispatch(
                saleActions.actionFinished({
                  actionFinished: { action: action.type, data: [sale] }
                })
              )
              return normalizationActions.upsertMany({
                entities: normalizeToSchema({
                  [this.entityFeatureKey]: [sale]
                })
              })
            }),
            catchError((error) => {
              console.error('Failed to update sale by id')
              return of(saleActions.updateOneByIdFailed())
            })
          )
      })
    )
  )
  onLoadMember = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.loadMember),
      withLatestFrom(
        this.store.select(
          configurationOptionSelectors.selectByKeyWithType(
            ConfigOptionsKeys.ALLOW_PENDING_REQUIRED_SALE_SKIPPING
          )
        )
      ),
      map(([action, allowPendingRequiredSaleSkipping]) => {
        const statuses = [StatusCode.INFLIGHT]
        if (!allowPendingRequiredSaleSkipping) {
          statuses.push(StatusCode.PENDING_REQUIRED)
        }

        return saleActions.fetchMany({
          params: RequestQueryBuilder.create({
            search: {
              customerPersonId: action.member.id,
              statusCode: {
                [CondOperator.IN]: statuses
              }
            },
            join: [
              { field: 'payments' },
              { field: 'saleItems' },
              { field: 'saleItems.product' },
              { field: 'saleItems.product.documents' },
              { field: 'saleItems.product.translations' },
              { field: 'saleItems.booking' },
              { field: 'saleItems.booking.product' }
            ]
          })
        })
      })
    )
  )

  onUpsertMany = createEffect(() =>
    this.actions$.pipe(
      ofType(saleActions.upsertMany),
      map((action) => {
        return saleActions.actionFinished({
          actionFinished: { action: action.type, data: action?.entities ?? [] }
        })
      })
    )
  )
}
